 import React, { useEffect, useState } from "react";
 import { useNavigate } from "react-router-dom";
 import * as XLSX from "xlsx";
 import api from "../api/api";

 const ROWS_PER_PAGE = 6;

export default function GrnPending() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    GIPNo: "",
    GIPDate: "",
    DCNo: "",
    DCDate: "",
    VName: "",
    PreparedBy: "",
    CheckedBy: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);

      const userName = sessionStorage.getItem("userName");
      const databaseKey = sessionStorage.getItem("databaseKey");
      const status = "Waiting for GRN";

      if (!userName || !databaseKey) {
        console.error("❌ Missing userName or databaseKey in sessionStorage");
        setLoading(false);
        return;
      }

      const res = await api.GetQcDashBoardValuesList_API(
        userName,
        status,
        databaseKey
      );

      const result =
        res?.GetQcDashBoardValuesList_API_APIjs?.Table || res?.Table || [];

      setData(result);
    } catch (error) {
      console.error("❌ Error fetching data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ✅ Filter data
  const filteredData = data.filter((row) => {
    return Object.keys(filters).every(key => {
      const filterValue = filters[key].toLowerCase();
      const rowValue = (row[key] || "").toString().toLowerCase();
      return filterValue === "" || rowValue.includes(filterValue);
    });
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // ✅ Pagination
  const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const paginatedData = filteredData.slice(startIndex, startIndex + ROWS_PER_PAGE);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // ✅ Export Excel
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "GRN Pending");
    XLSX.writeFile(wb, "GRN_Pending_List.xlsx");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-full mx-auto bg-white rounded-lg shadow-lg">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-400 to-blue-500 text-white p-4 rounded-t-lg flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white px-3 py-2 rounded-lg font-semibold flex items-center space-x-1 transition-all duration-200 shadow-md hover:shadow-lg text-sm mr-4"
            title="Go Back"
          >
            <span>⬅️</span>
            <span>Back</span>
          </button>
          <h1 className="text-xl font-bold flex-1 text-center">GRN Pending List</h1>
        </div>

        {/* Export Button in Header */}
        <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200 flex justify-end">
          <button
            onClick={exportToExcel}
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-3 py-2 rounded-lg font-semibold flex items-center space-x-1 transition-all duration-200 shadow-md hover:shadow-lg text-sm"
            title="Export to Excel"
          >
            <span>📊</span>
            <span>Export</span>
          </button>
        </div>

        {/* Table */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-pulse text-xl text-slate-600 font-semibold">🔄 Loading data...</div>
            </div>
          ) : (
            <div className="overflow-x-auto shadow-2xl rounded-2xl">
              <div className="max-h-[500px] overflow-y-auto">
                <table className="min-w-full bg-white border-collapse">
                  <thead className="bg-gradient-to-r from-slate-700 to-slate-800 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">#</th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">
                        <div className="flex items-center justify-between">
                          <span>GIP No</span>
                          <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="ml-2 text-white hover:text-slate-200 transition-colors"
                            title={showFilters ? "Hide Filters" : "Show Filters"}
                          >
                            <span className="text-xs">{showFilters ? "🔽" : "🔼"}</span>
                          </button>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">
                        <div className="flex items-center justify-between">
                          <span>GIP Date</span>
                          <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="ml-2 text-white hover:text-slate-200 transition-colors"
                            title={showFilters ? "Hide Filters" : "Show Filters"}
                          >
                            <span className="text-xs">{showFilters ? "🔽" : "🔼"}</span>
                          </button>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">
                        <div className="flex items-center justify-between">
                          <span>DC No</span>
                          <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="ml-2 text-white hover:text-slate-200 transition-colors"
                            title={showFilters ? "Hide Filters" : "Show Filters"}
                          >
                            <span className="text-xs">{showFilters ? "🔽" : "🔼"}</span>
                          </button>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">
                        <div className="flex items-center justify-between">
                          <span>DC Date</span>
                          <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="ml-2 text-white hover:text-slate-200 transition-colors"
                            title={showFilters ? "Hide Filters" : "Show Filters"}
                          >
                            <span className="text-xs">{showFilters ? "🔽" : "🔼"}</span>
                          </button>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">
                        <div className="flex items-center justify-between">
                          <span>Vendor</span>
                          <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="ml-2 text-white hover:text-slate-200 transition-colors"
                            title={showFilters ? "Hide Filters" : "Show Filters"}
                          >
                            <span className="text-xs">{showFilters ? "🔽" : "🔼"}</span>
                          </button>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">
                        <div className="flex items-center justify-between">
                          <span>Prepared By</span>
                          <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="ml-2 text-white hover:text-slate-200 transition-colors"
                            title={showFilters ? "Hide Filters" : "Show Filters"}
                          >
                            <span className="text-xs">{showFilters ? "🔽" : "🔼"}</span>
                          </button>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white border-r border-slate-600">
                        <div className="flex items-center justify-between">
                          <span>Checked By</span>
                          <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="ml-2 text-white hover:text-slate-200 transition-colors"
                            title={showFilters ? "Hide Filters" : "Show Filters"}
                          >
                            <span className="text-xs">{showFilters ? "🔽" : "🔼"}</span>
                          </button>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-bold text-white">Action</th>
                    </tr>
                    {showFilters && (
                      <tr className="bg-slate-100 border-t border-slate-300">
                        <th className="px-4 py-2 border-r border-slate-300"></th>
                        <th className="px-4 py-2 border-r border-slate-300">
                          <input
                            type="text"
                            placeholder="Filter..."
                            value={filters.GIPNo}
                            onChange={(e) => setFilters({ ...filters, GIPNo: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                          />
                        </th>
                        <th className="px-4 py-2 border-r border-slate-300">
                          <input
                            type="text"
                            placeholder="Filter..."
                            value={filters.GIPDate || ""}
                            onChange={(e) => setFilters({ ...filters, GIPDate: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                          />
                        </th>
                        <th className="px-4 py-2 border-r border-slate-300">
                          <input
                            type="text"
                            placeholder="Filter..."
                            value={filters.DCNo}
                            onChange={(e) => setFilters({ ...filters, DCNo: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                          />
                        </th>
                        <th className="px-4 py-2 border-r border-slate-300">
                          <input
                            type="text"
                            placeholder="Filter..."
                            value={filters.DCDate || ""}
                            onChange={(e) => setFilters({ ...filters, DCDate: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                          />
                        </th>
                        <th className="px-4 py-2 border-r border-slate-300">
                          <input
                            type="text"
                            placeholder="Filter..."
                            value={filters.VName}
                            onChange={(e) => setFilters({ ...filters, VName: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                          />
                        </th>
                        <th className="px-4 py-2 border-r border-slate-300">
                          <input
                            type="text"
                            placeholder="Filter..."
                            value={filters.PreparedBy || ""}
                            onChange={(e) => setFilters({ ...filters, PreparedBy: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                          />
                        </th>
                        <th className="px-4 py-2 border-r border-slate-300">
                          <input
                            type="text"
                            placeholder="Filter..."
                            value={filters.CheckedBy || ""}
                            onChange={(e) => setFilters({ ...filters, CheckedBy: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                          />
                        </th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((row, index) => (
                        <tr key={index} className={`transition-all duration-200 border-b border-slate-200 ${
                          index % 2 === 0
                            ? 'bg-white hover:bg-slate-100'
                            : 'bg-slate-50 hover:bg-slate-100'
                        }`}>
                          <td className="px-4 py-3 text-sm font-semibold text-slate-800 border-r border-slate-200">{startIndex + index + 1}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 border-r border-slate-200 font-medium">{row.GIPNo}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 border-r border-slate-200">{row.GIPDate}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 border-r border-slate-200 font-medium">{row.DCNo}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 border-r border-slate-200">{row.DCDate}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 border-r border-slate-200">{row.VName}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 border-r border-slate-200">{row.PreparedBy}</td>
                          <td className="px-4 py-3 text-sm text-slate-700 border-r border-slate-200">{row.CheckedBy}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              // onClick={() =>
                              //   navigate("/grn-pending-detail", { state: row })
                              // }
                              className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-3 py-1 rounded-lg font-semibold flex items-center space-x-1 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                              <span>📦</span>
                              <span>View</span>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="px-6 py-12 text-center text-slate-500 bg-slate-100">
                          <div className="flex flex-col items-center space-y-2">
                            <span className="text-4xl">📭</span>
                            <span>No matching records found</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          <div className="flex justify-between items-center mt-8 px-4">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-5 py-3 rounded-xl font-bold transition-all duration-200 flex items-center space-x-2 ${
                  currentPage === 1
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                }`}
              >
                <span className="text-lg">⬅️</span>
                <span>Previous</span>
              </button>

              <div className="flex items-center space-x-2">
                <span className="text-slate-600 font-semibold">Page</span>
                <span className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-xl font-bold shadow-md">
                  {currentPage} of {totalPages}
                </span>
              </div>

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-5 py-3 rounded-xl font-bold transition-all duration-200 flex items-center space-x-2 ${
                  currentPage === totalPages
                    ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                }`}
              >
                <span>Next</span>
                <span className="text-lg">➡️</span>
              </button>
            </div>

            <div className="text-sm text-slate-500 font-medium">
              Showing {startIndex + 1}-{Math.min(startIndex + ROWS_PER_PAGE, filteredData.length)} of {filteredData.length} records
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

